/**
 * InternalSection Component
 *
 * Displays internal ophthalmology examination data in a tabular format showing OD (right eye) and OS (left eye)
 * findings for different anatomical systems including vitreous, disc/optic nerve, C/D ratio, macula, vessels,
 * A/V ratio, and periphery. Also displays general examination information like type, indications, method, and dilation.
 * Provides an editable interface for updating internal examination findings.
 *
 * Follows Apple's design guidelines with consistent glass morphism UI and accessibility features.
 */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { motion } from 'framer-motion';
import { CheckCircle, Edit3, Loader2, Save, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface InternalData {
  // General Information
  ophtalDocumentationType?: string;
  ophtalIndications?: string;
  ophtalMethod?: string;
  ophtalDilationStatus?: string;
  ophtalDilationEye?: string;
  ophtalDilationDrops?: string;
  ophtalDilationAmount?: string;
  ophtalDilationStamp?: string;

  // Vitreous
  ophtalVitreousOD?: string;
  ophtalVitreousOS?: string;

  // Disc/Optic Nerve
  ophtalDiscOD?: string;
  ophtalDiscOS?: string;
  discODNotDocumentedMedicalReasons?: string;
  discOSNotDocumentedMedicalReasons?: string;

  // C/D Ratio
  cdRatioHorzOD?: string;
  cdRatioVertOD?: string;
  cdRatioHorzOS?: string;
  cdRatioVertOS?: string;
  cdRatioODNotDocumentedMedicalReasons?: string;
  cdRatioOSNotDocumentedMedicalReasons?: string;

  // Macula
  ophtalMaculaOD?: string;
  ophtalMaculaOS?: string;

  // Vessels
  ophtalVesselsOD?: string;
  ophtalVesselsOS?: string;

  // A/V Ratio
  avRatioOD?: string;
  avRatioOS?: string;

  // Periphery
  ophtalPeripheryOD?: string;
  ophtalPeripheryOS?: string;

  // Fundus Normal
  ophtalFundusNormalOD?: string;
  ophtalFundusNormalOS?: string;

  // Preview
  ophtalPreviewOd?: string;
  ophtalPreviewOs?: string;
  ophtalPreviewOdUrl?: string;
  ophtalPreviewOsUrl?: string;

  // Type
  ophthalTypeStandard?: string;
  ophthalTypeDrawing?: string;

  [key: string]: any; // Allow for other properties in the JSON
}

interface InternalApiResponse {
  testKey: string;
  data: InternalData[];
}

// Legacy data structure interface (for backward compatibility)
interface InternalLegacyData {
  ophtalDocumentationType?: string;
  ophtalIndications?: string;
  ophtalMethod?: string;
  ophtalDilationStatus?: string;
  ophtalDilationEye?: string;
  ophtalDilationDrops?: string;
  ophtalDilationAmount?: string;
  ophtalDilationStamp?: string;
  ophtalVitreousOD?: string;
  ophtalVitreousOS?: string;
  ophtalDiscOD?: string;
  ophtalDiscOS?: string;
  cdRatioHorzOD?: string;
  cdRatioVertOD?: string;
  cdRatioHorzOS?: string;
  cdRatioVertOS?: string;
  ophtalMaculaOD?: string;
  ophtalMaculaOS?: string;
  ophtalVesselsOD?: string;
  ophtalVesselsOS?: string;
  avRatioOD?: string;
  avRatioOS?: string;
  ophtalPeripheryOD?: string;
  ophtalPeripheryOS?: string;
  ophtalFundusNormalOD?: string;
  ophtalFundusNormalOS?: string;
  ophtalPreviewOd?: string;
  ophtalPreviewOs?: string;
  ophtalPreviewOdUrl?: string;
  ophtalPreviewOsUrl?: string;
  ophthalTypeStandard?: string;
  ophthalTypeDrawing?: string;
  [key: string]: any; // Allow for other properties in the JSON
}

// Nested structure interface (for pages[0].data format)
interface InternalNestedData {
  pages?: Array<{
    data?: InternalData;
    [key: string]: any;
  }>;
  [key: string]: any;
}

// Union type to handle both new and legacy formats, including array format
type InternalDataUnion =
  | InternalApiResponse
  | InternalLegacyData
  | InternalApiResponse[]
  | InternalNestedData
  | null;

interface InternalSectionProps {
  jsonContent: InternalDataUnion;
  isLoading?: boolean;
  error?: string | null;
  index?: number;
  onUpdate: (updatedData: InternalDataUnion) => Promise<void>;
  onAccept?: () => void;
}

const InternalSection = ({
  jsonContent,
  isLoading = false,
  error = null,
  index = 0,
  onUpdate,
  onAccept,
}: InternalSectionProps) => {
  const [currentData, setCurrentData] = useState<InternalData | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editValues, setEditValues] = useState<InternalData>({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Helper function to extract the actual internal data from either format
  const extractInternalData = useCallback(
    (data: InternalDataUnion): InternalData | null => {
      if (!data) return null;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(data) && data.length > 0) {
        const firstItem = data[0];
        if (
          'testKey' in firstItem &&
          'data' in firstItem &&
          Array.isArray(firstItem.data)
        ) {
          return firstItem.data[0] || null;
        }
      }

      // Check if it's the new API response format (single object)
      if ('testKey' in data && 'data' in data && Array.isArray(data.data)) {
        return data.data[0] || null;
      }

      // Check if it's the nested structure with pages[0].data
      if ('pages' in data && data.pages?.[0]?.data) {
        return data.pages[0].data;
      }

      // Legacy format: direct object with internal fields
      if (
        'ophtalVitreousOD' in data ||
        'ophtalDiscOD' in data ||
        'ophtalMaculaOD' in data
      ) {
        return data as InternalData;
      }

      return null;
    },
    []
  );

  // Helper function to create the appropriate data structure for updates
  const createUpdateData = useCallback(
    (updatedFields: InternalData): InternalDataUnion => {
      if (!jsonContent) return null;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(jsonContent) && jsonContent.length > 0) {
        const firstItem = jsonContent[0];
        if (
          'testKey' in firstItem &&
          'data' in firstItem &&
          Array.isArray(firstItem.data)
        ) {
          const updatedDataArray = [
            {
              ...firstItem.data[0],
              ...updatedFields,
            },
          ];

          return [
            {
              ...firstItem,
              data: updatedDataArray,
            },
          ];
        }
      }

      // Check if it's the new API response format (single object)
      if (
        'testKey' in jsonContent &&
        'data' in jsonContent &&
        Array.isArray(jsonContent.data)
      ) {
        const updatedDataArray = [
          {
            ...jsonContent.data[0],
            ...updatedFields,
          },
        ];

        return {
          ...jsonContent,
          data: updatedDataArray,
        };
      }

      // Check if it's the nested structure with pages[0].data
      if ('pages' in jsonContent && jsonContent.pages?.[0]?.data) {
        return {
          ...jsonContent,
          pages: [
            {
              ...jsonContent.pages[0],
              data: {
                ...jsonContent.pages[0].data,
                ...updatedFields,
              },
            },
          ],
        };
      }

      // Legacy format: return direct object
      return {
        ...jsonContent,
        ...updatedFields,
      };
    },
    [jsonContent]
  );

  // Update local state when jsonContent changes
  useEffect(() => {
    const extractedData = extractInternalData(jsonContent);
    setCurrentData(extractedData);
  }, [jsonContent, extractInternalData]);

  // Handle updates to the internal data
  const handleUpdate = useCallback(
    async (updatedFields: InternalData) => {
      if (!currentData) return;

      const updatedData = createUpdateData(updatedFields);

      // Update local state optimistically
      setCurrentData({
        ...currentData,
        ...updatedFields,
      });

      try {
        await onUpdate(updatedData);
      } catch (error) {
        // Revert local state on error
        const extractedData = extractInternalData(jsonContent);
        setCurrentData(extractedData);
        throw error;
      }
    },
    [currentData, jsonContent, onUpdate, createUpdateData, extractInternalData]
  );

  const handleEditClick = () => {
    // Initialize edit values with current data
    setEditValues(currentData || {});
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    setIsUpdating(true);
    try {
      await handleUpdate(editValues);
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update internal examination:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditDialogOpen(false);
    setEditValues({});
  };

  const handleInputChange = (field: string, value: string) => {
    setEditValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Define anatomical systems with their field mappings
  const anatomicalSystems = [
    {
      name: 'Vitreous',
      odField: 'ophtalVitreousOD',
      osField: 'ophtalVitreousOS',
      type: 'text',
    },
    {
      name: 'Disc/Optic Nerve',
      odField: 'ophtalDiscOD',
      osField: 'ophtalDiscOS',
      type: 'text',
    },
    {
      name: 'C/D Ratio',
      odField: 'cdRatioHorzOD',
      osField: 'cdRatioHorzOS',
      type: 'cd-ratio',
      vertOdField: 'cdRatioVertOD',
      vertOsField: 'cdRatioVertOS',
    },
    {
      name: 'Macula',
      odField: 'ophtalMaculaOD',
      osField: 'ophtalMaculaOS',
      type: 'text',
    },
    {
      name: 'Vessels',
      odField: 'ophtalVesselsOD',
      osField: 'ophtalVesselsOS',
      type: 'text',
    },
    {
      name: 'A/V Ratio',
      odField: 'avRatioOD',
      osField: 'avRatioOS',
      type: 'select',
      options: ['1/2', '2/3', '3/4', '4/5'],
    },
    {
      name: 'Periphery',
      odField: 'ophtalPeripheryOD',
      osField: 'ophtalPeripheryOS',
      type: 'text',
    },
  ];

  // Helper function to get display value with fallback
  const getDisplayValue = (value: string | undefined) => value || '—';

  // Helper function to format C/D ratio display
  const getCDRatioDisplay = (
    horz: string | undefined,
    vert: string | undefined
  ) => {
    if (!horz && !vert) return '—';
    if (horz && vert) return `${horz} / ${vert}`;
    return horz || vert || '—';
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.4, ease: 'easeOut' }}
        className="group">
        {/* Main card */}
        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-lg border border-white/30 dark:border-white/15 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all duration-300">
          {/* Inner highlight */}
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-t from-transparent via-transparent to-white/20 dark:to-white/8 pointer-events-none" />

          {/* Content */}
          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-slate-800 dark:text-slate-200 font-semibold text-xl tracking-wide">
                  Internal
                </h3>
                {isLoading && (
                  <div className="flex items-center gap-1 ml-2">
                    <Loader2 className="w-3 h-3 animate-spin text-eyevantage-primary" />
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      Loading...
                    </span>
                  </div>
                )}
              </div>
              {currentData && !error && (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick();
                    }}
                    size="sm"
                    variant="outline"
                    className="h-8 px-3 bg-slate-100/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-gray-400/40 dark:border-white/20 text-slate-700 dark:text-slate-300 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                    disabled={isLoading}>
                    <Edit3 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  {onAccept && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAccept();
                      }}
                      size="sm"
                      className="h-8 px-3 bg-emerald-100/50 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-emerald-600/60 dark:border-white/20 text-emerald-700 dark:text-emerald-700 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                      disabled={isLoading}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Accept
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="space-y-4">
              {error ? (
                <div className="text-slate-500 dark:text-slate-400 text-md italic">
                  {error === 'Data not found' ? (
                    'No internal examination data available yet'
                  ) : (
                    <div className="text-red-600 dark:text-red-400">
                      <span className="font-bold">Error:</span> {error}
                    </div>
                  )}
                </div>
              ) : isLoading ? (
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-md">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Fetching internal examination data...</span>
                </div>
              ) : currentData ? (
                <>
                  {/* General Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-slate-600 dark:text-slate-400">
                        Type
                      </Label>
                      <div className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50/50 dark:bg-slate-800/30 px-3 py-2 rounded-lg">
                        {getDisplayValue(currentData.ophtalDocumentationType)}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-slate-600 dark:text-slate-400">
                        Indications
                      </Label>
                      <div className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50/50 dark:bg-slate-800/30 px-3 py-2 rounded-lg">
                        {getDisplayValue(currentData.ophtalIndications)}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-slate-600 dark:text-slate-400">
                        Method
                      </Label>
                      <div className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50/50 dark:bg-slate-800/30 px-3 py-2 rounded-lg">
                        {getDisplayValue(currentData.ophtalMethod)}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-slate-600 dark:text-slate-400">
                        Dilation
                      </Label>
                      <div className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50/50 dark:bg-slate-800/30 px-3 py-2 rounded-lg">
                        {currentData.ophtalDilationStatus && (
                          <div className="space-y-1">
                            <div>
                              Status: {currentData.ophtalDilationStatus}
                            </div>
                            <div>Eye: {currentData.ophtalDilationEye}</div>
                            <div>Drops: {currentData.ophtalDilationDrops}</div>
                            <div>
                              Amount: {currentData.ophtalDilationAmount}
                            </div>
                            {currentData.ophtalDilationStamp && (
                              <div className="text-xs text-slate-500">
                                {currentData.ophtalDilationStamp}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Internal Examination Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          <th
                            style={{ width: '35%' }}
                            className="border border-gray-400/40 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-md font-semibold text-slate-700 dark:text-slate-300 text-center">
                            OD
                          </th>
                          <th
                            style={{ width: '30%' }}
                            className="border border-gray-400/40 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-md font-semibold text-slate-700 dark:text-slate-300 text-center">
                            System
                          </th>
                          <th
                            style={{ width: '35%' }}
                            className="border border-gray-400/40 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-md font-semibold text-slate-700 dark:text-slate-300 text-center">
                            OS
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {anatomicalSystems.map((system, idx) => (
                          <tr
                            key={system.name}
                            className={
                              idx % 2 === 0
                                ? 'bg-slate-50/30 dark:bg-slate-800/30'
                                : ''
                            }>
                            <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-md text-slate-700 dark:text-slate-300 text-center">
                              {system.type === 'cd-ratio'
                                ? getCDRatioDisplay(
                                    currentData[system.odField],
                                    currentData[system.vertOdField]
                                  )
                                : getDisplayValue(currentData[system.odField])}
                            </td>
                            <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-md font-medium text-slate-700 dark:text-slate-300 text-center">
                              <strong>{system.name}</strong>
                            </td>
                            <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-md text-slate-700 dark:text-slate-300 text-center">
                              {system.type === 'cd-ratio'
                                ? getCDRatioDisplay(
                                    currentData[system.osField],
                                    currentData[system.vertOsField]
                                  )
                                : getDisplayValue(currentData[system.osField])}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-slate-500 dark:text-slate-400 text-md italic">
                  No internal examination data available yet
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent
          className="max-w-5xl max-h-[80vh] overflow-y-auto bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg border border-white/30 dark:border-white/15"
          onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="text-slate-800 dark:text-slate-200 font-semibold text-xl">
              Edit Internal
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* General Information Section */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-md text-slate-600 dark:text-slate-400">
                    Type
                  </Label>
                  <Input
                    value={editValues.ophtalDocumentationType || ''}
                    onChange={(e) =>
                      handleInputChange(
                        'ophtalDocumentationType',
                        e.target.value
                      )
                    }
                    placeholder="e.g., Routine"
                    className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-md text-slate-600 dark:text-slate-400">
                    Indications
                  </Label>
                  <Input
                    value={editValues.ophtalIndications || ''}
                    onChange={(e) =>
                      handleInputChange('ophtalIndications', e.target.value)
                    }
                    placeholder="e.g., AION, Asteroid Hyalosis"
                    className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-md text-slate-600 dark:text-slate-400">
                    Method
                  </Label>
                  <Input
                    value={editValues.ophtalMethod || ''}
                    onChange={(e) =>
                      handleInputChange('ophtalMethod', e.target.value)
                    }
                    placeholder="e.g., SL: 78-90D, Indirect"
                    className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-md text-slate-600 dark:text-slate-400">
                    Dilation Status
                  </Label>
                  <Input
                    value={editValues.ophtalDilationStatus || ''}
                    onChange={(e) =>
                      handleInputChange('ophtalDilationStatus', e.target.value)
                    }
                    placeholder="e.g., Performed"
                    className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-md text-slate-600 dark:text-slate-400">
                    Dilation Eye
                  </Label>
                  <Input
                    value={editValues.ophtalDilationEye || ''}
                    onChange={(e) =>
                      handleInputChange('ophtalDilationEye', e.target.value)
                    }
                    placeholder="e.g., OU"
                    className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-md text-slate-600 dark:text-slate-400">
                    Dilation Drops
                  </Label>
                  <Input
                    value={editValues.ophtalDilationDrops || ''}
                    onChange={(e) =>
                      handleInputChange('ophtalDilationDrops', e.target.value)
                    }
                    placeholder="e.g., Tropicamide 1.0%"
                    className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-md text-slate-600 dark:text-slate-400">
                    Dilation Amount
                  </Label>
                  <Input
                    value={editValues.ophtalDilationAmount || ''}
                    onChange={(e) =>
                      handleInputChange('ophtalDilationAmount', e.target.value)
                    }
                    placeholder="e.g., 3 drops"
                    className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-md text-slate-600 dark:text-slate-400">
                    Dilation Stamp
                  </Label>
                  <Input
                    value={editValues.ophtalDilationStamp || ''}
                    onChange={(e) =>
                      handleInputChange('ophtalDilationStamp', e.target.value)
                    }
                    placeholder="e.g., 12:30 AM by Doctor, Demo A."
                    className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
              </div>
            </div>

            {/* Examination Findings Section */}
            <div className="space-y-4">
              {/* Table Header */}
              <div className="grid grid-cols-3 gap-4 pb-2 border-b border-gray-400/40 dark:border-slate-700">
                <div className="text-md font-semibold text-slate-700 dark:text-slate-300 text-center">
                  OD (Right Eye)
                </div>
                <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                  System
                </div>
                <div className="text-md font-semibold text-slate-700 dark:text-slate-300 text-center">
                  OS (Left Eye)
                </div>
              </div>

              {/* Anatomical Systems in Table Format */}
              <div className="space-y-3">
                {anatomicalSystems.map((system, idx) => (
                  <div
                    key={system.name}
                    className={`grid grid-cols-3 gap-2 p-0 rounded-lg`}>
                    {/* OD Input */}
                    <div className="space-y-1">
                      {system.type === 'cd-ratio' ? (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-md text-slate-500">
                              Horz
                            </Label>
                            <Input
                              value={editValues[system.odField] || ''}
                              onChange={(e) =>
                                handleInputChange(
                                  system.odField,
                                  e.target.value
                                )
                              }
                              placeholder="0.10"
                              className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15 text-center"
                            />
                          </div>
                          <div>
                            <Label className="text-md text-slate-500">
                              Vert
                            </Label>
                            <Input
                              value={editValues[system.vertOdField] || ''}
                              onChange={(e) =>
                                handleInputChange(
                                  system.vertOdField,
                                  e.target.value
                                )
                              }
                              placeholder="0.10"
                              className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15 text-center"
                            />
                          </div>
                        </div>
                      ) : system.type === 'select' ? (
                        <Select
                          value={editValues[system.odField] || ''}
                          onValueChange={(value) =>
                            handleInputChange(system.odField, value)
                          }>
                          <SelectTrigger className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15">
                            <SelectValue placeholder="Select ratio" />
                          </SelectTrigger>
                          <SelectContent>
                            {system.options?.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={editValues[system.odField] || ''}
                          onChange={(e) =>
                            handleInputChange(system.odField, e.target.value)
                          }
                          placeholder={`e.g., Clear, Normal, etc.`}
                          className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15 text-center"
                        />
                      )}
                    </div>

                    {/* System Name */}
                    <div className="flex items-center justify-center">
                      <Label className="text-lg font-bold text-slate-700 dark:text-slate-300">
                        <strong>{system.name}</strong>
                      </Label>
                    </div>

                    {/* OS Input */}
                    <div className="space-y-1">
                      {system.type === 'cd-ratio' ? (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-md text-slate-500">
                              Horz
                            </Label>
                            <Input
                              value={editValues[system.osField] || ''}
                              onChange={(e) =>
                                handleInputChange(
                                  system.osField,
                                  e.target.value
                                )
                              }
                              placeholder="0.10"
                              className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15 text-center"
                            />
                          </div>
                          <div>
                            <Label className="text-md text-slate-500">
                              Vert
                            </Label>
                            <Input
                              value={editValues[system.vertOsField] || ''}
                              onChange={(e) =>
                                handleInputChange(
                                  system.vertOsField,
                                  e.target.value
                                )
                              }
                              placeholder="0.10"
                              className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15 text-center"
                            />
                          </div>
                        </div>
                      ) : system.type === 'select' ? (
                        <Select
                          value={editValues[system.osField] || ''}
                          onValueChange={(value) =>
                            handleInputChange(system.osField, value)
                          }>
                          <SelectTrigger className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15">
                            <SelectValue placeholder="Select ratio" />
                          </SelectTrigger>
                          <SelectContent>
                            {system.options?.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={editValues[system.osField] || ''}
                          onChange={(e) =>
                            handleInputChange(system.osField, e.target.value)
                          }
                          placeholder={`e.g., Clear, Normal, etc.`}
                          className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15 text-center"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-white/80 dark:bg-slate-800/80 border border-gray-400/40 dark:border-white/20">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleSaveEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-500 hover:to-blue-800 text-white">
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InternalSection;
